import { supabase } from '../supabase/client';
import type { LokalerEntwurf } from './lokal';
import type { Protokoll } from './typen';
import { pruefeAbschlussBereit } from './unterschriften';

export interface FinalisierErgebnis {
  ok: boolean;
  pdf_path?: string;
  /** Adressen, an die das PDF tatsächlich rausging (leer, wenn schon früher versendet wurde). */
  mails?: string[];
  /** Pflichtparteien ohne E-Mail-Adresse — die bekommen ihr Beweisdokument nicht. */
  uebersprungen?: string[];
  /** Klartext-Warnung der Function, z. B. „abgeschlossen, aber an niemanden versendet". */
  hinweis?: string;
  /**
   * Bei Teilerfolg (z. B. 1 von 2 Adressen erreicht, s. Fund Task 4/Schritt 3): die
   * Adressen, an die der Versand fehlschlug — der Vermerk bleibt trotzdem gesetzt, weil
   * mindestens einer das Protokoll bekommen hat.
   */
  nichtErreicht?: string[];
  /**
   * Rollout-Fund 2: Die Neuerzeugung des PDFs ergab ANDERE Bytes als das bereits
   * archivierte Dokument — typischerweise, weil ein Verweis auf Stammdaten (Mitarbeiter,
   * Objekt, Mieter) durch eine legitime Löschung auf null gegangen ist und im neuen PDF
   * deshalb „Erfasst von: unbekannt" bzw. „Objekt nicht zugeordnet" stünde. Die Function
   * hat die archivierte Datei deshalb NICHT ersetzt (s. archiv.ts dort).
   */
  archivAbweichung?: boolean;
}

// Diese Felder verwaltet AUSSCHLIESSLICH der Server — der Client kennt ihren aktuellen
// Stand nie, weil es laut Design keinen bidirektionalen Abgleich gibt (siehe lokal.ts).
//
// pdf_path/pdf_hash/drive_file_id/mail_versendet_am setzt die Edge Function
// uebergabe-finalisieren, NACHDEM sie serverseitig fertig ist (PDF erzeugt, Mail
// verschickt). Server-Schutz gegen Doppelversand ist ein atomares
// `.update({mail_versendet_am: ...}).is('mail_versendet_am', null)` in der Function
// (der "Zuschlag") — würde der Client dieses Feld hier auf seinen lokalen (stets null)
// Stand zurückschreiben, verliert dieser Schutz seine Wirkung: ein Retry nach einer
// verlorenen Server-Antwort (Funkloch NACH erfolgreichem Versand) würde die Mail an
// Mieter und Eigentümer ein zweites Mal auslösen.
//
// created_at (Fix-Runde 1, Review): die Kopfzeile entsteht bereits beim Bestätigen der
// Vorauswahl (legeKopfzeileAn, DB-Default now()) — ein lokaler Entwurf trägt aber
// seinen EIGENEN, im Browser berechneten created_at-Zeitpunkt (s. lokal.ts
// erzeugeEntwurf), der beim Abschluss sonst den echten, serverseitigen Anlagezeitpunkt
// überschreiben würde.
const SERVERSEITIGE_FELDER = ['pdf_path', 'pdf_hash', 'drive_file_id', 'mail_versendet_am', 'created_at'] as const;
type ServerseitigesFeld = (typeof SERVERSEITIGE_FELDER)[number];
export type ProtokollFuerUpsert = Omit<Protokoll, ServerseitigesFeld>;

/**
 * Reduziert ein Protokoll auf die Felder, die der Client beim Abschluss schreiben darf.
 * Lässt die fünf serverseitig verwalteten Felder bewusst WEG (statt sie explizit auf
 * null zu setzen) — Postgres/PostgREST bauen einen Upsert nur für die im Payload
 * enthaltenen Spalten (`ON CONFLICT ... DO UPDATE SET` nur für gesendete Spalten),
 * bereits gesetzte Serverwerte bleiben also unangetastet. Als benannte Funktion, damit
 * ein späterer Umbau von `schliesseAb` diesen Schutz nicht still wieder verliert — s.
 * Test in abschluss.test.ts ("Upsert-Rumpf enthält keines der fünf serverseitigen Felder").
 */
export function ohneServerFelder(p: Protokoll): ProtokollFuerUpsert {
  const rest: Partial<Protokoll> = { ...p };
  for (const feld of SERVERSEITIGE_FELDER) delete rest[feld];
  return rest as ProtokollFuerUpsert;
}

/**
 * Auffälligkeiten eines Abschlusses als Klartext für die Oberfläche; leerer String, wenn
 * alles glatt lief (dann wird bewusst NICHTS gemeldet — eine Meldung nach jeder Übergabe
 * stumpft ab, s. Fund Kl5). Gemeinsam genutzt von Assistent und Liste, damit beide Wege
 * dieselben Warnungen zeigen.
 */
export function abschlussMeldungen(erg: FinalisierErgebnis): string {
  const teile: string[] = [];
  if (erg.hinweis) teile.push(erg.hinweis);
  // Rollout-Fund 2: Das Archiv-PDF steht unverändert — gemeldet wird, dass es NICHT durch
  // den abweichenden neuen Stand ersetzt wurde. Genau darum geht es: Niemand soll später
  // ein stillschweigend neu gebackenes Beweisdokument in der Hand halten.
  if (erg.archivAbweichung) {
    teile.push('Das archivierte PDF weicht vom aktuellen Datenstand ab und wurde nicht ersetzt.');
  }
  if (erg.uebersprungen?.length) {
    teile.push(`Ohne E-Mail-Adresse — diese Beteiligten haben das Protokoll NICHT erhalten: ${erg.uebersprungen.join(', ')}`);
  }
  // Teilerfolg (Fund Task 4/Schritt 3): der Vermerk bleibt gesetzt, weil mindestens eine
  // Adresse erreicht wurde — die nicht erreichten dürfen deshalb trotzdem nicht spurlos
  // verschwinden.
  if (erg.nichtErreicht?.length) {
    teile.push(`Versand an diese Adresse(n) fehlgeschlagen: ${erg.nichtErreicht.join(', ')}`);
  }
  return teile.join('\n\n');
}

export interface AbschlussDeps {
  ladeHoch(pfad: string, blob: Blob): Promise<void>;
  speichereProtokoll(p: ProtokollFuerUpsert): Promise<void>;
  finalisiere(id: string): Promise<FinalisierErgebnis>;
}

// Reihenfolge ist bewusst: Dateien zuerst, Datensatz danach, Function zuletzt.
// Jeder Schritt ist wiederholbar (Storage upsert, Protokoll upsert, Function
// idempotent) — bricht das Netz mittendrin weg, wird beim nächsten Versuch alles
// noch einmal gefahren, ohne Doppelungen.
//
// Idempotenz-Hinweis (auch für "Jetzt senden" in UebergabeListe.tsx relevant): läuft
// dieser Aufruf ein zweites Mal für ein Protokoll, dessen Server-Lauf beim ersten Mal
// bereits vollständig durchlief (nur die Antwort ging im Funkloch verloren), meldet die
// Function trotzdem `ok:true` zurück — sie erkennt am unangetastet gebliebenen
// `mail_versendet_am`, dass schon verschickt wurde, und überspringt den erneuten
// Mailversand. Das PDF wird dabei neu erzeugt, aber seit der Archiv-Bremse (Rollout-Fund 2,
// supabase/functions/uebergabe-finalisieren/archiv.ts) nicht mehr in jedem Fall
// geschrieben: ergibt die Neuerzeugung andere Bytes als das bereits archivierte Dokument,
// bleibt die vorhandene Datei stehen und die Function meldet `archivAbweichung`. Der
// Aufrufer muss diesen Fall NICHT gesondert behandeln: ein `ok:true` ohne Exception heißt
// immer "der lokale Entwurf darf gelöscht werden", unabhängig davon, ob tatsächlich neu
// versendet wurde.
export async function schliesseAb(entwurf: LokalerEntwurf, deps: AbschlussDeps): Promise<FinalisierErgebnis> {
  // Unterschriften-Sperre (Fund K1): Diese Prüfung steckte nur im Assistenten und band
  // damit ausschließlich den Weg über Schritt 6. „Jetzt senden" in der Liste ruft
  // schliesseAb direkt auf — ein bei Schritt 2 abgebrochener Entwurf ließ sich darüber
  // endgültig abschließen: PDF ohne Unterschriften, Mail an die Parteien, Entwurf danach
  // gelöscht. Deshalb steht sie hier, vor JEDEM Seiteneffekt (kein Upload, kein Upsert,
  // kein Function-Aufruf) — kein weiterer Aufrufer kann sie umgehen.
  const bereitschaft = pruefeAbschlussBereit(entwurf.protokoll);
  if (!bereitschaft.bereit) {
    throw new Error(bereitschaft.grund ?? 'Es fehlen Pflichtunterschriften.');
  }

  for (const [pfad, blob] of Object.entries(entwurf.fotos)) await deps.ladeHoch(pfad, blob);
  for (const [pfad, blob] of Object.entries(entwurf.unterschriften)) await deps.ladeHoch(pfad, blob);

  await deps.speichereProtokoll(ohneServerFelder({ ...entwurf.protokoll, status: 'abgeschlossen' }));
  return deps.finalisiere(entwurf.id);
}

export function echteDeps(): AbschlussDeps {
  return {
    async ladeHoch(pfad, blob) {
      const { error } = await supabase.storage.from('uebergaben')
        .upload(pfad, blob, { upsert: true, contentType: blob.type || 'application/octet-stream' });
      if (error) throw error;
    },
    async speichereProtokoll(p) {
      const { error } = await supabase.from('uebergabeprotokolle').upsert(p);
      if (error) throw error;
    },
    async finalisiere(id) {
      const { data, error } = await supabase.functions.invoke('uebergabe-finalisieren', { body: { protokollId: id } });
      if (error) throw error;
      return data as FinalisierErgebnis;
    },
  };
}
